import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, matchPath } from "react-router-dom";
import { findSegment, SERVICES, BRAND } from "../lib/data";

const BASE = "https://genesisltd.in";

const STATIC_SEO = {
  "/": {
    title: "Genesis Mall | All-in-One Business Center by Genesis Group",
    description:
      "Genesis Mall by Genesis Group — an all-in-one business center connecting businesses, services, shopping, affiliate opportunities and rural enterprise across a modern Bharat. Rooted in Bharat, connected to opportunity.",
  },
  "/about": {
    title: "About Genesis Group | Our Story, Vision & Mission",
    description:
      "Learn about Genesis Group of Companies — a multi-sector business house building opportunities across finance, agro, e-commerce, green energy, education and more. Discover our vision, mission and journey.",
  },
  "/businesses": {
    title: "Our Businesses | The Genesis Ecosystem of 10 Verticals",
    description:
      "Explore the Genesis ecosystem of ten business verticals — from Finance (Loan Doctor) and Agro-Dairy to E-commerce, Green Energy, Healthcare, Education, Import-Export, Marketing and IT/VFX.",
  },
  "/mall": {
    title: "Genesis Mall | All-in-One Business Center",
    description:
      "Discover Genesis Mall — an all-in-one business center bringing businesses, services and shopping together across a modern Bharat. Browse categories and connect with the Genesis ecosystem.",
  },
  "/rural-connect": {
    title: "Rural Connect | Genesis Group for Bharat",
    description:
      "Genesis Rural Connect empowers farmers, artisans, MSMEs and local retailers by linking rural enterprise to wider markets, logistics and digital reach. Swadeshi at heart.",
  },
  "/opportunity": {
    title: "Genesis Business Network | Affiliate & Opportunity",
    description:
      "Join the Genesis Business Network — refer, build a team and grow through the Genesis ecosystem. Transparent ranks and rewards with milestone verification and administrative approval before payout.",
  },
  "/affiliate": {
    title: "Affiliate Program | Genesis Business Network",
    description:
      "Become a Genesis affiliate — share your referral link, build your team and progress through ranks. Rewards are subject to plan terms, milestone verification and admin approval.",
  },
  "/ranks-rewards": {
    title: "Ranks & Rewards | Grow With Genesis",
    description:
      "Grow with Genesis through a clear rank journey — START, BUILD, GROW and ACHIEVE. Understand how ranks and rewards work, with milestone verification and administrative approval before payout.",
  },
  "/how-it-works": {
    title: "How It Works | The Genesis Ecosystem Explained",
    description:
      "See how the Genesis ecosystem works — from joining and referring to building a team, progressing ranks and earning rewards across the All-in-One Business Center.",
  },
  "/franchise": {
    title: "Franchise Opportunities | Partner With Genesis Group",
    description:
      "Partner with Genesis Group through franchise and distributor opportunities across education, agro & cow-dung eco-industry, healthcare, e-commerce and more. Explore plans and apply.",
  },
  "/investors": {
    title: "Investor Relations | Genesis Group",
    description:
      "Investor relations at Genesis Group — explore our multi-sector growth vision, diversified revenue streams and long-term roadmap. Submit an enquiry or request a meeting.",
  },
  "/services": {
    title: "Financial Services | Loan Doctor by Genesis",
    description:
      "Complete financial and growth solutions from Loan Doctor by Genesis — business and personal loans, project funding, government subsidy schemes, low-CIBIL solutions and insurance advisory.",
  },
  "/careers": {
    title: "Careers & Internships | Genesis Group",
    description:
      "Build your career with Genesis Group — full-time roles and student internships across sales, education, finance, production and marketing, with certification and live projects.",
  },
  "/contact": {
    title: "Contact Genesis Group | Get In Touch",
    description:
      "Contact Genesis Group — reach the team by phone, WhatsApp or email for business, franchise, investment or general enquiries across the All-in-One Business Center.",
  },
  "/login": {
    title: "Sign In | Genesis Group Account",
    description:
      "Sign in to your Genesis Group account to access the All-in-One Business Center — manage your team, ranks, rewards, payouts, documents and notifications.",
  },
  "/register": {
    title: "Create Account | Join Genesis Group",
    description:
      "Create your free Genesis Group account and join the All-in-One Business Center as a customer, agent or affiliate. Shop, connect, refer and grow across Bharat.",
  },
};

const NOINDEX_PREFIXES = ["/dashboard", "/u/"];

function resolveSeo(pathname) {
  if (STATIC_SEO[pathname]) return { ...STATIC_SEO[pathname], path: pathname, noindex: false };

  const seg = matchPath("/businesses/:slug", pathname);
  if (seg) {
    const s = findSegment(seg.params.slug);
    if (s)
      return {
        title: `${s.name} | Genesis Group`,
        description: s.intro,
        path: pathname,
        noindex: false,
      };
  }

  const svc = matchPath("/services/:slug", pathname);
  if (svc) {
    const s = SERVICES.find((x) => x.slug === svc.params.slug);
    if (s)
      return {
        title: `${s.title} | Loan Doctor by Genesis`,
        description: s.desc,
        path: pathname,
        noindex: false,
      };
  }

  const noindex = NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));
  return {
    title: `${BRAND.mall} | ${BRAND.businessCenter} | ${BRAND.name}`,
    description: STATIC_SEO["/"].description,
    path: pathname,
    noindex,
  };
}

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const { title, description, path, noindex } = resolveSeo(pathname);
  const clean = path === "/" ? "/" : path.replace(/\/+$/, "");
  const url = `${BASE}${clean}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.full} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};
